import { motion } from 'framer-motion';
import useGameStore from '@/store/gameStore';

function ScoringPhase() {
  const { gameState } = useGameStore();

  if (!gameState?.round) return null;

  const isSuccess = gameState.round.result === 'SUCCESS';
  const isSkipped = gameState.round.result === 'SKIPPED';

  const getResultEmoji = () => {
    if (isSuccess) return '🎉';
    if (isSkipped) return '😔';
    return '😢';
  };

  const getResultText = () => {
    if (isSuccess) return 'Correct!';
    if (isSkipped) return 'Skipped';
    return 'Wrong Answer';
  };

  const getResultColor = () => {
    if (isSuccess) return 'text-success-600';
    if (isSkipped) return 'text-warning-600';
    return 'text-danger-600';
  };

  return (
    <div className="py-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center"
      >
        {/* Result Emoji */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="text-8xl mb-4"
        >
          {getResultEmoji()}
        </motion.div>

        {/* Result Text */}
        <h2 className={`text-3xl font-bold mb-4 ${getResultColor()}`}>{getResultText()}</h2>

        {/* Round Details */}
        <div className="max-w-md mx-auto space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">The word was:</p>
            <p className="text-2xl font-bold text-gray-800">{gameState.round.targetWord}</p>
          </div>

          {gameState.round.guess && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">The guess was:</p>
              <p className="text-2xl font-bold text-gray-800">{gameState.round.guess}</p>
            </div>
          )}

          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">Guesser:</p>
            <p className="text-lg font-semibold text-gray-800">
              {gameState.players[gameState.round.activeGuesserId]?.name}
            </p>
          </div>

          {/* Clues Summary */}
          <div className="p-4 bg-primary-50 rounded-lg">
            <p className="text-sm text-primary-600 mb-2">Clues Given:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {Object.entries(gameState.round.clues).map(([userId, clue]) => (
                <span
                  key={userId}
                  className={`
                    px-2 py-1 rounded-full text-xs font-semibold
                    ${
                      clue.status === 'VISIBLE'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700 line-through'
                    }
                  `}
                >
                  {clue.text}
                </span>
              ))}
            </div>
          </div>

          {/* Current Score */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-success-50 rounded-lg">
              <p className="text-2xl font-bold text-success-600">{gameState.deck.successCount}</p>
              <p className="text-sm text-success-600">Correct</p>
            </div>
            <div className="p-4 bg-danger-50 rounded-lg">
              <p className="text-2xl font-bold text-danger-600">{gameState.deck.failedCount}</p>
              <p className="text-sm text-danger-600">Wrong/Skipped</p>
            </div>
          </div>

          {/* Progress */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">Progress:</p>
            <div className="flex justify-between text-sm">
              <span>
                Round {gameState.round.number} of {gameState.config.totalRounds}
              </span>
              <span>{gameState.deck.remaining} cards remaining</span>
            </div>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 transition-all duration-500"
                style={{
                  width: `${(gameState.round.number / gameState.config.totalRounds) * 100}%`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Next Round Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-6 text-sm text-gray-500"
        >
          {gameState.round.number < gameState.config.totalRounds
            ? 'Moving to the next round soon...'
            : 'This was the final round!'}
        </motion.div>
      </motion.div>
    </div>
  );
}

export default ScoringPhase;
